"use client";

import { useEffect, useState } from "react";

import { getProductsBySkus, type ProductSummary } from "@/app/actions/product";
import type { ListingProductRating } from "@/domain/product-listing";

export type ListingRatings = ReadonlyMap<string, ListingProductRating>;

const NO_RATINGS: ListingRatings = new Map();

export function useListingRatings(skus: string[]): ListingRatings {
	const [ratings, setRatings] = useState<ListingRatings>(NO_RATINGS);
	const key = [...skus].sort().join(",");

	useEffect(() => {
		if (!key) {
			setRatings(NO_RATINGS);
			return;
		}

		let cancelled = false;

		getProductsBySkus(key.split(","))
			.then((response) => {
				if (cancelled) return;

				// A failed read must not read as "nothing has been reviewed".
				if (response.status !== "success") {
					console.error(
						"client:plp-product-ratings",
						response.message
					);
					return;
				}

				setRatings(toRatings(response.products));
			})
			.catch((error) => {
				console.error("client:plp-product-ratings", error);
			});

		return () => {
			cancelled = true;
		};
	}, [key]);

	return ratings;
}

function toRatings(products: ProductSummary[]): ListingRatings {
	const ratings = new Map<string, ListingProductRating>();

	for (const product of products) {
		// A product with no reviews yet has nothing to draw.
		if (!product.numRatings || typeof product.starsRating !== "number") {
			continue;
		}

		ratings.set(product.sku, {
			value: product.starsRating,
			count: product.numRatings
		});
	}

	return ratings;
}
